import * as React from 'react';
import {
    Box, Button, Card, CardContent, FormControl, Grid, IconButton, InputLabel, MenuItem, Select, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';

/** params.cache: what each model call caches, always for 1 hour. */
export type AiCacheMode = 'OFF' | 'TOOLS' | 'SYSTEM';

export const AI_CACHE_MODES: { value: AiCacheMode; label: string; help: string }[] = [
    { value: 'SYSTEM', label: 'Tool definitions + system prompt', help: 'Everything before the messages (incl. context tools). The usual choice.' },
    { value: 'TOOLS', label: 'Tool definitions only', help: 'When the system prompt changes between runs.' },
    { value: 'OFF', label: 'Nothing', help: 'No prompt caching.' },
];

/** What a context entry reads: a tool the hub calls (with arguments) or a resource it reads (by URI). */
export type ContextSource = 'tool' | 'resource';

/** params.contextTools[] as edited (strings while typing). */
export interface ContextToolForm {
    source: ContextSource;
    server: string;
    tool: string;
    resource: string;
    args: string;
    title: string;
    ttlMinutes: string;
}

export const EMPTY_CONTEXT_TOOL: ContextToolForm = { source: 'tool', server: '', tool: '', resource: '', args: '{}', title: '', ttlMinutes: '30' };

export function contextToolsFromParams(value: unknown): ContextToolForm[] {
    if (!Array.isArray(value)) return [];
    return value.filter(v => v && typeof v === 'object').map(v => {
        const t = v as Record<string, unknown>;
        // The hub reads a tool when both are set.
        const source: ContextSource = !t.tool && t.resource ? 'resource' : 'tool';
        return {
            source,
            server: String(t.server ?? ''),
            tool: String(t.tool ?? ''),
            resource: String(t.resource ?? ''),
            args: t.args && typeof t.args === 'object' ? JSON.stringify(t.args) : '{}',
            title: String(t.title ?? ''),
            ttlMinutes: t.ttlMinutes != null ? String(t.ttlMinutes) : '30',
        };
    });
}

/** The params.contextTools value, or an error message. */
export function contextToolsToParams(tools: ContextToolForm[]): { value: Record<string, unknown>[]; error: string | null } {
    const value: Record<string, unknown>[] = [];
    for (let i = 0; i < tools.length; i++) {
        const t = tools[i];
        const n = i + 1;
        const ttl = Number(t.ttlMinutes || '30');
        if (!Number.isFinite(ttl) || ttl < 1 || ttl > 1440) return { value, error: `Context entry ${n}: refresh must be 1–1440 minutes.` };
        if (t.source === 'resource') {
            if (!t.server.trim() || !t.resource.trim()) return { value, error: `Context entry ${n}: server and resource URI are required.` };
            value.push({ server: t.server.trim(), resource: t.resource.trim(), title: t.title.trim() || t.resource.trim(), ttlMinutes: ttl });
            continue;
        }
        if (!t.server.trim() || !t.tool.trim()) return { value, error: `Context entry ${n}: server and tool are required.` };
        let args: unknown = {};
        try {
            args = t.args.trim() ? JSON.parse(t.args) : {};
        } catch {
            return { value, error: `Context entry ${n}: arguments must be a JSON object.` };
        }
        if (!args || typeof args !== 'object' || Array.isArray(args)) return { value, error: `Context entry ${n}: arguments must be a JSON object.` };
        value.push({ server: t.server.trim(), tool: t.tool.trim(), args, title: t.title.trim() || t.tool.trim(), ttlMinutes: ttl });
    }
    return { value, error: null };
}

/**
 * Agent editor › Prompt: prompt caching and the context entries — MCP tools the hub calls, or MCP resources it reads,
 * before a run and appends to the system prompt (Betty's FAQ tool, Ace's app guide resource): no tool turn, and the
 * text is part of the cached prefix.
 */
export default function AgentPromptContext({ cache, contextTools, onChange }: {
    cache: AiCacheMode;
    contextTools: ContextToolForm[];
    onChange: (p: { cache?: AiCacheMode; contextTools?: ContextToolForm[] }) => void;
}) {
    const update = (i: number, p: Partial<ContextToolForm>) =>
        onChange({ contextTools: contextTools.map((t, j) => (j === i ? { ...t, ...p } : t)) });
    return (
        <Card variant="outlined">
            <CardContent>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                        <Typography variant="subtitle1">Prompt caching</Typography>
                        <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                            <InputLabel id="agent-cache-label">Cache</InputLabel>
                            <Select labelId="agent-cache-label" label="Cache" value={cache} onChange={e => onChange({ cache: e.target.value as AiCacheMode })}>
                                {AI_CACHE_MODES.map(m => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
                            </Select>
                        </FormControl>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                            {AI_CACHE_MODES.find(m => m.value === cache)?.help} Kept 1 hour: a write costs 2× input, every read about 5–10 %.
                            The system prompt can only be cached together with the tool definitions that precede it; conversations are never cached.
                        </Typography>
                    </Grid>
                    <Grid item xs={12} md={8}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography variant="subtitle1">Context tools &amp; resources</Typography>
                            <Button size="small" startIcon={<AddIcon />} onClick={() => onChange({ contextTools: [...contextTools, EMPTY_CONTEXT_TOOL] })}>
                                Add
                            </Button>
                        </Stack>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                            What the hub fetches before each run and appends to the system prompt under its title: a tool it calls with
                            fixed arguments (e.g. the FAQ) or a resource it reads by URI (e.g. Ace's app guide; fixed reference text is
                            better a resource — see the server's live tools &amp; resources). The server must be linked to the agent (Tools
                            tab); the tool needn't be offered to the model. The text is shared by all users (no bound arguments) and
                            refreshed after the given minutes.
                        </Typography>
                        {contextTools.length === 0 && <Typography variant="body2" color="text.secondary">None.</Typography>}
                        <Stack gap={1.5}>
                            {contextTools.map((t, i) => (
                                <Box key={i} sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(150px, 0.9fr) 1.2fr 1fr 1.2fr 1.6fr 0.7fr auto' }, gap: 1, alignItems: 'center' }}>
                                    <FormControl size="small">
                                        <InputLabel id={`context-source-${i}`}>Type</InputLabel>
                                        <Select labelId={`context-source-${i}`} label="Type" value={t.source}
                                            onChange={e => update(i, { source: e.target.value as ContextSource })}>
                                            <MenuItem value="tool">Tool</MenuItem>
                                            <MenuItem value="resource">Resource</MenuItem>
                                        </Select>
                                    </FormControl>
                                    <TextField size="small" label="Server" value={t.server} onChange={e => update(i, { server: e.target.value })} />
                                    {t.source === 'resource' ? (
                                        <TextField size="small" label="Resource URI" value={t.resource} placeholder="betup://assistant/app-guide"
                                            onChange={e => update(i, { resource: e.target.value })}
                                            InputProps={{ sx: { fontFamily: 'monospace' } }} sx={{ gridColumn: { md: 'span 2' } }} />
                                    ) : (
                                        <>
                                            <TextField size="small" label="Tool" value={t.tool} onChange={e => update(i, { tool: e.target.value })} />
                                            <TextField size="small" label="Arguments (JSON)" value={t.args} onChange={e => update(i, { args: e.target.value })}
                                                InputProps={{ sx: { fontFamily: 'monospace' } }} />
                                        </>
                                    )}
                                    <TextField size="small" label="Title in the prompt" value={t.title} onChange={e => update(i, { title: e.target.value })} />
                                    <TextField size="small" label="Refresh (min)" value={t.ttlMinutes}
                                        onChange={e => update(i, { ttlMinutes: e.target.value.replace(/[^\d]/g, '') })} />
                                    <Tooltip title="Remove">
                                        <IconButton onClick={() => onChange({ contextTools: contextTools.filter((_, j) => j !== i) })}><DeleteIcon /></IconButton>
                                    </Tooltip>
                                </Box>
                            ))}
                        </Stack>
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    );
}
